import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Word.css';
import { getWordsByUnit, getUnitIdFromStorage } from '../../services/wordService';
import Alert from '../../components/Alert';
import SettingsModal from '../../components/SettingsModal';
import { dictationConfig, readingConfig } from '../../config/settingsConfig';

// 开发环境下导入测试工具
if (process.env.NODE_ENV === 'development') {
  import('../../utils/testHelpers');
}

const Word = () => {
  const navigate = useNavigate();
  const [wordsData, setWordsData] = useState(null);
  const [loading, setLoading] = useState(false); // 🔧 改为false，避免初始闪烁
  
  const [error, setError] = useState(null);
  const [unitId, setUnitId] = useState(null);
  
  // 🔧 使用 useRef 跟踪请求状态，避免闭包问题
  const isRequestInProgressRef = useRef(false);
  const loadWordsCalledRef = useRef(false); // 跟踪 loadWords 是否已被调用过
  
  // 🔧 从localStorage加载已选词汇（普通模式）
  const loadSelectedWordsFromStorage = () => {
    try {
      const saved = localStorage.getItem('selectedWords');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('加载已选词汇失败:', error);
      return [];
    }
  };

  // 🔧 从localStorage加载Quiz模式的已选词汇
  const loadQuizSelectedWordsFromStorage = () => {
    try {
      const saved = localStorage.getItem('selectedWordsForQuiz');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('加载Quiz已选词汇失败:', error);
      return [];
    }
  };

  // 🔧 保存已选词汇到localStorage（普通模式）
  const saveSelectedWordsToStorage = (words) => {
    try {
      localStorage.setItem('selectedWords', JSON.stringify(words));
    } catch (error) {
      console.error('保存已选词汇失败:', error);
    }
  };

  // 🔧 保存Quiz模式的已选词汇到localStorage
  const saveQuizSelectedWordsToStorage = (words) => {
    try {
      localStorage.setItem('selectedWordsForQuiz', JSON.stringify(words));
    } catch (error) {
      console.error('保存Quiz已选词汇失败:', error);
    }
  };

  // 🔧 根据模式决定初始加载哪个存储
  // 先检查是否为Quiz模式，如果是则从Quiz存储加载，否则从普通存储加载
  const getInitialSelectedWords = () => {
    const quizMode = localStorage.getItem('wordSelectionMode');
    if (quizMode === 'quiz') {
      return loadQuizSelectedWordsFromStorage();
    }
    return loadSelectedWordsFromStorage();
  };

  // 🔧 根据 localStorage 判断初始的 Quiz 模式状态
  const getInitialQuizMode = () => {
    const quizMode = localStorage.getItem('wordSelectionMode');
    return quizMode === 'quiz';
  };

  // 新增状态管理
  const [selectedWords, setSelectedWords] = useState(getInitialSelectedWords()); // 存储选中的单词，根据模式从不同存储加载
  const [selectedWordItems, setSelectedWordItems] = useState([]); // 存储第三部分选中的单词
  const [deletingWords, setDeletingWords] = useState([]); // 存储正在删除动画中的单词
  const [isInverseMode, setIsInverseMode] = useState(false); // 反选模式是否激活
  const [isClearing, setIsClearing] = useState(false); // 是否正在执行清空操作
  const [isInitialized, setIsInitialized] = useState(false); // 是否已经初始化加载
  const [isRequestInProgress, setIsRequestInProgress] = useState(false); // 是否有请求正在进行
  const [isContentReady, setIsContentReady] = useState(false); // 🔧 新增：内容是否准备好显示
  const [showAlert, setShowAlert] = useState(false); // 提示框显示状态
  const [showDictationSettings, setShowDictationSettings] = useState(false); // 听写设置弹窗显示状态
  const [showReadingSettings, setShowReadingSettings] = useState(false); // 领读设置弹窗显示状态
  const [selectedLessonId, setSelectedLessonId] = useState(null); // 当前选中的课时ID
  
  // Quiz自选模式相关状态
  const [isQuizSelectionMode, setIsQuizSelectionMode] = useState(getInitialQuizMode()); // 🔧 是否为Quiz选择模式，根据localStorage动态初始化

  // 加载词汇数据的函数
  const loadWords = async (forceReload = false) => {
    try {
      // 🔧 防止重复请求 - 使用 ref 避免闭包问题
      if (isRequestInProgressRef.current) {
        return;
      }

      // 从localStorage获取单元ID
      const savedUnitId = getUnitIdFromStorage();
      if (!savedUnitId) {
        setError('未找到选择的单元，请先选择学习单元');
        setIsContentReady(true); // 🔧 即使出错也要显示内容
        return;
      }
      
      // 🔧 优化：更简洁的重复请求检查
      if (!forceReload && unitId === savedUnitId && wordsData && isInitialized) {
        setIsContentReady(true);
        return;
      }
      
      // 🔧 检查是否可以从 sessionStorage 复用 Quiz 设置弹窗的数据
      const cachedData = sessionStorage.getItem('quizUnitWordsData');
      if (cachedData && !forceReload) {
        try {
          const { unitId: cachedUnitId, data: cachedWordsData, timestamp } = JSON.parse(cachedData);
          // 检查缓存是否有效（5分钟内）
          const cacheAge = Date.now() - timestamp;
          const maxCacheAge = 5 * 60 * 1000; // 5分钟
          
          if (cachedUnitId === savedUnitId && cacheAge < maxCacheAge) {
            setWordsData(cachedWordsData);
            setUnitId(savedUnitId);
            setIsInitialized(true);
            setIsContentReady(true);
            
            // 设置默认选中第一个课时
            if (cachedWordsData && cachedWordsData.lessons && cachedWordsData.lessons.length > 0) {
              if (!selectedLessonId) {
                setSelectedLessonId(cachedWordsData.lessons[0].lesson_id);
              }
            }
            return;
          }
        } catch (err) {
          console.error('解析缓存数据失败:', err);
          // 继续正常加载流程
        }
      }

      // 🔧 使用 ref 标记请求状态
      isRequestInProgressRef.current = true;
      setIsRequestInProgress(true);
      setLoading(true);
      setError(null);
      setUnitId(savedUnitId);
      
      // 🔧 添加最小加载时间，确保平滑过渡
      const startTime = Date.now();
      const minLoadingTime = 300; // 最小显示300ms加载状态
      
      // 获取单元词汇数据，只获取激活的词汇
      const data = await getWordsByUnit(savedUnitId, { is_active: true, source_type: '1' });
      
      // 确保最小加载时间
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minLoadingTime) {
        await new Promise(resolve => setTimeout(resolve, minLoadingTime - elapsedTime));
      }
      
      setWordsData(data);
      setIsInitialized(true);
      setIsContentReady(true); // 🔧 数据加载完成后标记内容准备好
      
      // 🔧 如果有课时数据，默认选中第一个课时
      // forceReload 时强制重新选择第一个课时（切换单元时）
      if (data && data.lessons && data.lessons.length > 0) {
        if (forceReload || !selectedLessonId) {
          setSelectedLessonId(data.lessons[0].lesson_id);
        }
      }
    } catch (err) {
      console.error('加载词汇数据失败:', err);
      setError('加载词汇数据失败，请检查网络连接');
      setIsContentReady(true); // 🔧 即使出错也要显示内容
    } finally {
      setLoading(false);
      setIsRequestInProgress(false);
      // 🔧 清除 ref 标记
      isRequestInProgressRef.current = false;
    }
  };


  // 🔧 持续监听 wordSelectionMode 的变化，动态切换模式和加载对应存储
  useEffect(() => {
    // 定时检查 localStorage 中的模式标记
    const checkMode = () => {
      const quizMode = localStorage.getItem('wordSelectionMode');
      const shouldBeQuizMode = quizMode === 'quiz';
      
      // 只有模式真正改变时才更新状态和加载数据
      if (shouldBeQuizMode !== isQuizSelectionMode) {
        setIsQuizSelectionMode(shouldBeQuizMode);
        
        if (shouldBeQuizMode) {
          // 🔧 切换到Quiz模式时，从Quiz存储加载已选词汇
          const quizWords = loadQuizSelectedWordsFromStorage();
          setSelectedWords(quizWords);
        } else {
          // 🔧 切换到普通模式时，从普通存储加载已选词汇
          const normalWords = loadSelectedWordsFromStorage();
          setSelectedWords(normalWords);
        }
      }
    };
    
    // 首次检查
    checkMode();
    
    // 设置定时器，每100ms检查一次模式变化
    const intervalId = setInterval(checkMode, 100);
    
    // 监听页面刷新/关闭事件
    const handleBeforeUnload = () => {
      // 刷新或关闭页面时，清除Quiz选择模式标记
      const currentMode = localStorage.getItem('wordSelectionMode');
      if (currentMode === 'quiz') {
        localStorage.removeItem('wordSelectionMode');
        // 也清除可能存在的临时选择数据
        localStorage.removeItem('quizCustomSelectedWords');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      // 清除定时器
      clearInterval(intervalId);
      
      // 组件卸载时清理（用户通过侧边栏或其他方式离开页面）
      const currentMode = localStorage.getItem('wordSelectionMode');
      if (currentMode === 'quiz') {
        localStorage.removeItem('wordSelectionMode');
        // 注意：这里不清除 quizCustomSelectedWords，因为可能是通过"选择完成"按钮正常退出
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isQuizSelectionMode]); // 🔧 依赖 isQuizSelectionMode，确保状态同步

  // 监听单元变化事件，统一处理数据加载
  useEffect(() => {
    let timeoutId;
    let hasReceivedEvent = false;

    const handleUnitChange = (event) => {
      const { unitId: newUnitId } = event.detail;
      hasReceivedEvent = true;
      
      // 🔧 清除延迟检查，因为已经接收到事件
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      // 🔧 使用 ref 检查请求状态，避免闭包问题
      if (newUnitId && newUnitId !== unitId && !isRequestInProgressRef.current) {
        // 🔧 切换单元时，Quiz模式要清空当前组件的选择状态
        // localStorage 的清理已在 Layout.jsx 全局处理
        const quizMode = localStorage.getItem('wordSelectionMode');
        if (quizMode === 'quiz') {
          setSelectedWords([]);
          console.log('🔄 Word组件：清空单元切换后的选择状态');
        }
        
        // 🔧 重新加载时要强制刷新，因为单元变了
        // 传入 forceReload=true，会在加载完成后自动选中第一个课时
        loadWords(true);
      } else if (newUnitId === unitId && wordsData) {
        // 🔧 如果单元ID相同且已有数据，直接标记内容准备好
        setIsContentReady(true);
      }
    };

    // 添加自定义事件监听器
    window.addEventListener('unitChanged', handleUnitChange);

    // 🔧 优化：延迟检查策略，只在没有接收到事件时执行一次
    timeoutId = setTimeout(() => {
      // 🔧 使用 ref 检查是否已调用过 loadWords
      if (!hasReceivedEvent && !loadWordsCalledRef.current && !isRequestInProgressRef.current) {
        loadWordsCalledRef.current = true; // 标记已调用
        loadWords();
      } else {
        // 🔧 如果条件不满足但有数据，仍要显示内容
        if (wordsData) {
          setIsContentReady(true);
        }
      }
    }, 500); // 🔧 增加延迟到500ms，给unitChanged事件更多时间

    // 清理函数
    return () => {
      window.removeEventListener('unitChanged', handleUnitChange);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []); // 只在组件挂载时执行一次

  // 🔧 监听selectedWords变化，根据模式保存到对应的localStorage
  useEffect(() => {
    if (isQuizSelectionMode) {
      // Quiz模式：保存到Quiz存储
      saveQuizSelectedWordsToStorage(selectedWords);
    } else {
      // 普通模式：保存到普通存储
      saveSelectedWordsToStorage(selectedWords);
    }
  }, [selectedWords, isQuizSelectionMode]);

  // 处理单词点击选择
  const handleWordClick = (word) => {
    const wordId = word.id;
    const isSelected = selectedWords.includes(wordId);
    
    // 🔧 在Quiz模式下，禁止选择is_translate为false的单词
    if (isQuizSelectionMode && word.is_translate === false) {
      return; // 直接返回，不做任何操作
    }
    
    // 手动选择单词时，取消反选模式
    setIsInverseMode(false);
    
    if (isSelected) {
      // 如果已选中，则取消选择
      setSelectedWords(prev => prev.filter(id => id !== wordId));
    } else {
      // 如果未选中，则添加到选中列表
      setSelectedWords(prev => [...prev, wordId]);
    }
  };

  // 处理第三部分单词项的点击（删除功能）
  const handleWordItemClick = (wordId) => {
    // 如果已经在删除动画中或正在清空，则忽略点击
    if (deletingWords.includes(wordId) || isClearing) {
      return;
    }
    
    // 开始删除动画
    setDeletingWords(prev => [...prev, wordId]);
    
    // 延迟执行删除操作，让动画完成
    setTimeout(() => {
      // 从selectedWords中移除这个单词
      setSelectedWords(prev => prev.filter(id => id !== wordId));
      // 清除删除动画状态
      setDeletingWords(prev => prev.filter(id => id !== wordId));
    }, 600); // 动画持续时间600ms，与CSS动画协调
  };

  // 处理清空已选词汇
  const handleClearSelectedWords = () => {
    if (isClearing || selectedWords.length === 0) {
      return;
    }

    // 🔧 根据模式清空对应的localStorage存储
    if (isQuizSelectionMode) {
      localStorage.removeItem('selectedWordsForQuiz');
    } else {
      localStorage.removeItem('selectedWords');
    }
    
    setIsClearing(true);
    const selectedWordsData = getSelectedWordsData();
    const startTime = Date.now();
    const timeLimit = 1500; // 2秒时间限制
    let processedCount = 0;
    let isBatchMode = false;
    
    // 逐个触发删除动画
    selectedWordsData.forEach((word, index) => {
      const delay = index * 150; // 每个单词间隔150ms
      
      setTimeout(() => {
        const elapsed = Date.now() - startTime;
        
        // 如果超过2秒且还有剩余单词，切换到批量模式
        if (elapsed >= timeLimit && index < selectedWordsData.length - 1 && !isBatchMode) {
          isBatchMode = true;
          
          // 批量清空剩余的单词（从当前索引开始的所有剩余单词）
          const remainingWords = selectedWordsData.slice(index);
          
          // 同时添加所有剩余单词到删除动画状态
          setDeletingWords(prev => [...prev, ...remainingWords.map(w => w.id)]);
          
          // 600ms后同时移除所有剩余单词
          setTimeout(() => {
            const remainingIds = remainingWords.map(w => w.id);
            setSelectedWords(prev => prev.filter(id => !remainingIds.includes(id)));
            setDeletingWords(prev => prev.filter(id => !remainingIds.includes(id)));
            setIsClearing(false);
          }, 600);
          
          return; // 跳过单个处理逻辑
        }
        
        // 如果已经在批量模式，跳过单个处理
        if (isBatchMode) {
          return;
        }
        
        // 正常的单个删除动画
        setDeletingWords(prev => [...prev, word.id]);
        
        setTimeout(() => {
          setSelectedWords(prev => prev.filter(id => id !== word.id));
          setDeletingWords(prev => prev.filter(id => id !== word.id));
          processedCount++;
          
          // 如果是最后一个单词且没有进入批量模式，结束清空状态
          if (processedCount === selectedWordsData.length && !isBatchMode) {
            setIsClearing(false);
          }
        }, 600); // 与CSS动画时间协调
      }, delay);
    });
  };

  // 获取所有选中的单词详细信息
  const getSelectedWordsData = () => {
    if (!wordsData || !wordsData.lessons) return [];
    
    // 🔧 Quiz模式下只显示当前单元的选中单词
    if (isQuizSelectionMode) {
      const currentUnitSelectedIds = getCurrentUnitSelectedWords();
      const allWords = [];
      wordsData.lessons.forEach(lesson => {
        lesson.words.forEach(word => {
          if (currentUnitSelectedIds.includes(word.id)) {
            allWords.push(word);
          }
        });
      });
      return allWords;
    }
    
    // 普通模式下显示所有选中的单词（跨单元）
    const allWords = [];
    wordsData.lessons.forEach(lesson => {
      lesson.words.forEach(word => {
        if (selectedWords.includes(word.id)) {
          allWords.push(word);
        }
      });
    });
    return allWords;
  };

  // 获取所有单词的辅助函数
  const getAllWords = () => {
    if (!wordsData || !wordsData.lessons) return [];
    const allWords = [];
    wordsData.lessons.forEach(lesson => {
      if (lesson.words) {
        // 🔧 在Quiz模式下，只返回is_translate不为false的单词
        const words = isQuizSelectionMode 
          ? lesson.words.filter(word => word.is_translate !== false)
          : lesson.words;
        allWords.push(...words);
      }
    });
    return allWords;
  };

  // 🔧 获取当前单元选中的单词ID列表
  const getCurrentUnitSelectedWords = () => {
    const allWords = getAllWords();
    const currentUnitWordIds = allWords.map(word => word.id);
    return selectedWords.filter(id => currentUnitWordIds.includes(id));
  };

  // 获取所有黑体词（word_type_id = 1）
  const getBoldWords = () => {
    return getAllWords().filter(word => word.word_type_id === 1);
  };

  // 检查是否所有单词都已选中
  const isAllWordsSelected = () => {
    const allWords = getAllWords();
    if (allWords.length === 0) return false;
    return allWords.every(word => selectedWords.includes(word.id));
  };

  // 检查是否只选中了全部黑体词（没有非黑体词被选中，且所有黑体词都被选中）
  const isBoldWordsSelected = () => {
    const boldWords = getBoldWords();
    const boldWordIds = boldWords.map(word => word.id);
    
    if (boldWords.length === 0) return false;
    
    // 检查是否所有黑体词都被选中（只需要这一个条件即可）
    const allBoldSelected = boldWordIds.every(id => selectedWords.includes(id));
    
    return allBoldSelected;
  };

  // 处理全选功能
  const handleSelectAll = () => {
    const allWords = getAllWords();
    const allWordIds = allWords.map(word => word.id);
    
    // 取消反选模式
    setIsInverseMode(false);
    
    if (isAllWordsSelected()) {
      // 如果全部已选中，则取消全选（只清除当前单元的单词）
      setSelectedWords(prev => prev.filter(id => !allWordIds.includes(id)));
    } else {
      // 否则选中所有单词（保留其他单元的选择，添加当前单元的单词）
      setSelectedWords(prev => {
        // 先移除当前单元的所有单词ID（避免重复）
        const withoutCurrent = prev.filter(id => !allWordIds.includes(id));
        // 再添加当前单元的所有单词ID
        return [...withoutCurrent, ...allWordIds];
      });
    }
  };

  // 处理反选功能
  const handleSelectInverse = () => {
    const allWords = getAllWords();
    const allWordIds = allWords.map(word => word.id);
    
    if (isInverseMode) {
      // 如果已经是反选模式，则取消并清空（只清空当前单元的选择）
      setIsInverseMode(false);
      setSelectedWords(prev => prev.filter(id => !allWordIds.includes(id)));
    } else {
      // 获取当前单元中未选中的单词ID
      const unselectedWordIds = allWordIds.filter(id => !selectedWords.includes(id));
      
      // 保留其他单元的选择，只反选当前单元的单词
      setSelectedWords(prev => {
        // 移除当前单元的所有单词
        const withoutCurrent = prev.filter(id => !allWordIds.includes(id));
        // 添加当前单元未选中的单词
        return [...withoutCurrent, ...unselectedWordIds];
      });
      setIsInverseMode(true);
    }
  };

  // 处理黑体词选择功能
  const handleSelectBoldWords = () => {
    const allWords = getAllWords();
    const allWordIds = allWords.map(word => word.id);
    const boldWords = getBoldWords();
    const boldWordIds = boldWords.map(word => word.id);
    
    // 取消反选模式
    setIsInverseMode(false);
    
    if (isBoldWordsSelected()) {
      // 如果当前只选中了全部黑体词，则清空选择（只清空当前单元的黑体词）
      setSelectedWords(prev => prev.filter(id => !boldWordIds.includes(id)));
    } else {
      // 否则只选中所有黑体词（保留其他单元的选择，只替换当前单元的选择为黑体词）
      setSelectedWords(prev => {
        // 移除当前单元的所有单词
        const withoutCurrent = prev.filter(id => !allWordIds.includes(id));
        // 添加当前单元的黑体词
        return [...withoutCurrent, ...boldWordIds];
      });
    }
  };

  // Quiz选择完成处理
  const handleQuizSelectionComplete = () => {
    // 🔧 Quiz模式下，只检查当前单元的选择
    const currentUnitSelectedWords = getCurrentUnitSelectedWords();
    
    if (currentUnitSelectedWords.length === 0) {
      setShowAlert(true);
      return;
    }

    // 获取选中的单词完整数据
    const selectedWordsData = getSelectedWordsData();
    
    // 保存到localStorage供Quiz设置弹窗使用
    localStorage.setItem('quizCustomSelectedWords', JSON.stringify(selectedWordsData));
    
    // 清除选择模式标记
    localStorage.removeItem('wordSelectionMode');
    
    // 返回到Solo页面
    navigate('/solo');
  };

  // 🔧 第二部分按钮处理函数
  const handleWordReading = () => {
    // 检查是否有选中的单词
    if (selectedWords.length === 0) {
      // 显示提示框
      setShowAlert(true);
      return;
    }
    
    // 显示领读设置弹窗
    setShowReadingSettings(true);
  };

  const handleWordDictation = () => {
    // 检查是否有选中的单词
    if (selectedWords.length === 0) {
      // 显示提示框
      setShowAlert(true);
      return;
    }
    
    // 显示听写设置弹窗
    setShowDictationSettings(true);
  };

  // 处理听写设置弹窗关闭
  const handleDictationSettingsClose = () => {
    setShowDictationSettings(false);
  };

  // 处理领读设置弹窗关闭
  const handleReadingSettingsClose = () => {
    setShowReadingSettings(false);
  };

  // 处理开始听写
  const handleDictationStart = (settings) => {
    // 获取选中的单词数据
    const selectedWordsData = getSelectedWordsData();
    
    // 关闭弹窗
    setShowDictationSettings(false);
    
    // 将选中的单词数据映射为听写数据格式
    let wordsForDictation = selectedWordsData.map(word => ({
      id: word.id,
      title: word.title,
      word_type_id: word.word_type_id,
      phonetic: word.phonetic,
      en_title: word.en_title,
      audio_id: word.audio_id,
      audio: word.audio  // 🔧 添加完整的audio对象
    }));
    
    // 根据设置决定播放顺序：asc 正序；random 乱序
    if (settings?.order === 'random') {
      // 🎲 随机打乱单词顺序 - 使用 Fisher-Yates 洗牌算法
      const shuffled = [...wordsForDictation];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      wordsForDictation = shuffled;
    }
    
    // 使用 sessionStorage 存储数据，避免 URL 过长导致 431 错误
    const dictationData = {
      words: wordsForDictation,
      settings: settings
    };
    
    sessionStorage.setItem('dictationData', JSON.stringify(dictationData));
    navigate('/dictation');
  };

  // 处理开始领读
  const handleReadingStart = (settings) => {
    // 获取选中的单词数据
    const selectedWordsData = getSelectedWordsData();
    
    // 关闭弹窗
    setShowReadingSettings(false);
    
    // 将选中的单词数据映射为领读数据格式
    let wordsForReading = selectedWordsData.map(word => ({
      id: word.id,
      title: word.title,
      word_type_id: word.word_type_id,
      phonetic: word.phonetic,
      en_title: word.en_title,
      audio_id: word.audio_id,
      audio: word.audio,
      images: word.images  // 🖼️ 添加图片数据
    }));
    
    // 根据设置决定播放顺序：asc 正序；random 乱序
    if (settings?.order === 'random') {
      const shuffled = [...wordsForReading];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      wordsForReading = shuffled;
    }
    
    // 使用 sessionStorage 存储数据，避免 URL 过长导致 431 错误
    const readingData = {
      words: wordsForReading,
      settings: settings
    };
    
    sessionStorage.setItem('readingData', JSON.stringify(readingData));
    navigate('/reading');
  };

  // 检查某个课时是否全选
  const isLessonAllSelected = (lesson) => {
    if (!lesson.words || lesson.words.length === 0) return false;
    return lesson.words.every(word => selectedWords.includes(word.id));
  };

  // 处理课时全选/取消全选
  const handleLessonSelectAll = (lesson) => {
    // 🔧 在Quiz模式下，只处理is_translate不为false的单词
    const availableWords = isQuizSelectionMode 
      ? lesson.words.filter(word => word.is_translate !== false)
      : lesson.words;
    const lessonWordIds = availableWords.map(word => word.id);
    const isAllSelected = isLessonAllSelected(lesson);
    
    if (isAllSelected) {
      // 取消全选：从selectedWords中移除这个课时的所有单词
      setSelectedWords(prev => prev.filter(id => !lessonWordIds.includes(id)));
    } else {
      // 全选：将这个课时的所有单词添加到selectedWords中
      setSelectedWords(prev => {
        const newSelected = [...prev];
        lessonWordIds.forEach(id => {
          if (!newSelected.includes(id)) {
            newSelected.push(id);
          }
        });
        return newSelected;
      });
    }
  };

  // 渲染课时标签
  const renderLessonTabs = () => {
    if (!wordsData || !wordsData.lessons || wordsData.lessons.length === 0) {
      return null;
    }

    return (
      <div className="lesson-tabs">
        {wordsData.lessons.map((lesson) => (
          <div
            key={lesson.lesson_id}
            className={`lesson-tab ${selectedLessonId === lesson.lesson_id ? 'lesson-tab-active' : ''}`}
            onClick={() => setSelectedLessonId(lesson.lesson_id)}
          >
            {lesson.lesson_title}
          </div>
        ))}
      </div>
    );
  };

  // 渲染单词列表
  const renderWordsList = () => {
    // 🔧 如果内容还没准备好，显示占位符而不是加载状态
    if (!isContentReady) {
      return (
        <div className="words-placeholder">
          <div className="placeholder-content">
            <div className="placeholder-icon">📚</div>
            <p>准备加载词汇内容...</p>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="words-loading">
          <div className="loading-spinner"></div>
          <p>正在加载词汇...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="words-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>重新加载</button>
        </div>
      );
    }

    if (!wordsData || !wordsData.lessons || wordsData.lessons.length === 0) {
      return (
        <div className="words-empty">
          <p>该单元暂无词汇数据</p>
        </div>
      );
    }

    // 找到当前选中的课时
    const currentLesson = wordsData.lessons.find(lesson => lesson.lesson_id === selectedLessonId);
    
    if (!currentLesson) {
      return (
        <div className="words-empty">
          <p>请选择一个课时</p>
        </div>
      );
    }

    const isAllSelected = isLessonAllSelected(currentLesson);

    return (
      <div className={`words-content ${isContentReady ? 'words-content-ready' : ''}`}>
        <div className="lessons-container">
          <div className="lesson-section">
            <div className="lesson-header">
              <div 
                className={`lesson-select-all ${isAllSelected ? 'lesson-select-all-checked' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleLessonSelectAll(currentLesson);
                }}
                title={isAllSelected ? '取消全选' : '全选本课时单词'}
              >
                {isAllSelected && <span className="select-all-check">✓</span>}
              </div>
              <h3 className="lesson-title">{currentLesson.lesson_title}</h3>
            </div>
            <div className="words-grid">
            {currentLesson.words.map((word) => {
              const isSelected = selectedWords.includes(word.id);
              const isBold = word.word_type_id === 1; // 黑体字标识
              // 🔧 在Quiz模式下，is_translate为false的单词不可选
              const isDisabled = isQuizSelectionMode && word.is_translate === false;
              return (
                <div 
                  key={word.id} 
                  className={`word-card ${isSelected ? 'word-card-selected' : ''} ${isDisabled ? 'word-card-disabled' : ''}`}
                  onClick={() => handleWordClick(word)}
                  title={isDisabled ? '此单词不可用于PK比赛' : ''}
                >
                  <div className="word-main">
                    <div className={`word-english ${isBold ? 'word-bold' : ''}`}>
                      {word.title}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 渲染第三部分 - 选中单词列表
  const renderSelectedWordsList = () => {
    const selectedWordsData = getSelectedWordsData();
    
    if (selectedWordsData.length === 0) {
      return (
        <div className="selected-words-container">
          <div className="selected-words-header">
            <h3>已选词汇 (0)</h3>
          </div>
          <div className="selected-words-list">
            <div className="selected-words-empty">
              <p>请从左侧选择单词</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="selected-words-container">
        <div className="selected-words-header">
          <h3>已选词汇 ({selectedWordsData.length})</h3>
        </div>
        <div className="selected-words-list">
          {selectedWordsData.map((word) => {
            const isDeleting = deletingWords.includes(word.id);
            const isBold = word.word_type_id === 1; // 黑体字标识
            return (
              <div 
                key={word.id} 
                className={`selected-word-item ${isDeleting ? 'selected-word-item-deleting' : ''}`}
                onClick={() => handleWordItemClick(word.id)}
              >
                <div className="selected-word-content">
                  <div className={`selected-word-english ${isBold ? 'word-bold' : ''}`}>{word.title}</div>
                </div>
              </div>
            );
          })}
        </div>
        {selectedWordsData.length > 0 && (
          <button 
            className="clear-selected-button"
            onClick={handleClearSelectedWords}
            disabled={isClearing}
          >
            {isClearing ? '清空中...' : '清空已选内容'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="word-container">
      {/* 左侧区域 */}
      <div className="word-left">
        {/* 第一部分 - 主要内容区 */}
        <div className="word-section-1">
          {renderLessonTabs()}
          <div className="word-section-1-scroll">
            {renderWordsList()}
          </div>
          <div className="word-section-1-bottom">
            {/* 底部10%的白色不透明区域 - 添加选择控件 */}
            <div className="selection-controls">
              <label className="selection-control">
                <input 
                  type="checkbox" 
                  onChange={handleSelectAll}
                  checked={isAllWordsSelected()}
                />
                <span>全选</span>
              </label>
              
              <label className="selection-control">
                <input 
                  type="checkbox" 
                  onChange={handleSelectInverse}
                  checked={isInverseMode}
                />
                <span>反选</span>
              </label>
              
              <label className="selection-control">
                <input 
                  type="checkbox" 
                  onChange={handleSelectBoldWords}
                  checked={isBoldWordsSelected()}
                />
                <span>黑体词</span>
              </label>
            </div>
          </div>
        </div>
        
        {/* 第二部分 - 底部控制区 */}
        <div className="word-section-2">
          <div className="section2-buttons-container">
            {isQuizSelectionMode ? (
              // Quiz选择模式下显示单个"选择完成"按钮
              <button 
                className="section2-button section2-button-full"
                onClick={handleQuizSelectionComplete}
                disabled={getCurrentUnitSelectedWords().length === 0}
                title={getCurrentUnitSelectedWords().length === 0 ? '请先在当前单元选择至少一个词汇' : '完成选择并返回'}
              >
                选择完成
              </button>
            ) : (
              // 正常模式下显示两个按钮
              <>
                <button 
                  className="section2-button section2-button-left"
                  onClick={handleWordReading}
                >
                  单词领读
                </button>
                <button 
                  className="section2-button section2-button-right"
                  onClick={handleWordDictation}
                >
                  单词听写
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* 右侧区域 */}
      <div className="word-right">
        {renderSelectedWordsList()}
      </div>
      
      {/* 提示框 */}
      <Alert
        show={showAlert}
        message="请先选择单词"
        onHide={() => setShowAlert(false)}
        type="warning"
        duration={1200}
      />
      
      {/* 听写设置弹窗 */}
      <SettingsModal
        isOpen={showDictationSettings}
        onClose={handleDictationSettingsClose}
        onStart={handleDictationStart}
        title={dictationConfig.title}
        settingItems={dictationConfig.settingItems}
        defaultSettings={dictationConfig.defaultSettings}
      />
      
      {/* 领读设置弹窗 */}
      <SettingsModal
        isOpen={showReadingSettings}
        onClose={handleReadingSettingsClose}
        onStart={handleReadingStart}
        title={readingConfig.title}
        settingItems={readingConfig.settingItems}
        defaultSettings={readingConfig.defaultSettings}
      />
    </div>
  );
};

export default Word;
